import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    DatePicker,
    DateValidationError,
    DatePickerProps,
} from '@mui/x-date-pickers';
import { Controller, FieldValues } from 'react-hook-form';
import { DateTime } from 'luxon';

import { getDateFormat } from 'common/utils/getDateFormat';
import { valueToDateTime } from 'common/utils/formatDate';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

interface IProps extends Omit<DatePickerProps, 'name'> {
    name: string;
}

export const DatePickerField = <TFieldValues extends FieldValues>({
    control: formControl,
    label,
    name,
    ...other
}: IProps & TControllerProps<TFieldValues>) => {
    const dateFormat = getDateFormat();
    const [translate] = useTranslation();

    const control = asTypedControl<string | Date>(name, formControl);

    const [error, setError] = useState<DateValidationError | null>(null);

    const errorMessage = useMemo(() => {
        switch (error) {
            case 'maxDate':
                return translate('maxDateMessage');

            case 'minDate':
                return translate('minDateMessage');

            case 'invalidDate': {
                return translate('invalidDateMessage');
            }

            default:
                return '';
        }
    }, [error]);

    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState: { error: fieldError } }) => (
                <DatePicker
                    {...field}
                    format={dateFormat}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            margin: 'normal',
                            label: label,
                            error: !!fieldError || !!error,
                            helperText: fieldError?.message || errorMessage,
                            sx: {
                                '& .MuiPickersInputBase-sectionsContainer': {
                                    py: 2,
                                },
                            },
                        },
                    }}
                    // Make sure we have a luxon DateTime object
                    value={valueToDateTime(field.value) as DateTime}
                    onError={(newError) => setError(newError)}
                    {...other}
                />
            )}
        />
    );
};
